import tosEnFallback from "@/assets/legal/wahoo_tos_run_en.txt?raw";

const TOS_BASE_URL = "https://cdn.wahooligan.com/wahoo-cloud/static/documents";

// Locales for which a translated KICKR RUN TOS is hosted on the CDN. Any locale
// not listed here is shown the English document.
const TOS_LOCALES = ["en", "pl"];

export interface TermsOfService {
  /** Version recorded on acceptance — must be identical across translations. */
  version: string;
  paragraphs: string[];
  /** Language actually shown, for acceptance auditing (may differ from request). */
  locale: string;
}

/**
 * Splits a TOS document into paragraphs and reads its version. The version lives
 * in an optional leading `<META version=N>` line; when that header is absent the
 * version defaults to "1" and — importantly — the first paragraph is kept rather
 * than dropped as document content.
 */
export function parseTos(body: string): { version: string; paragraphs: string[] } {
  const paragraphs = body.split("\n\n");
  const metaMatch = paragraphs[0]?.match(/<META version=(\d+)>/);
  if (metaMatch) {
    paragraphs.shift();
    return { version: metaMatch[1], paragraphs };
  }
  return { version: "1", paragraphs };
}

async function fetchTosDocument(locale: string): Promise<string | undefined> {
  try {
    const response = await fetch(`${TOS_BASE_URL}/${locale}/wahoo_tos_run.txt`);
    if (!response.ok) {
      return undefined;
    }
    return await response.text();
  } catch {
    return undefined;
  }
}

/**
 * Resolves the KICKR RUN terms of service for the given locale with a three-tier
 * fallback: localized CDN document → English CDN document → bundled English copy.
 * The bundled copy guarantees a real (never blank) document at the acceptance gate
 * even if the CDN is unreachable.
 */
export async function getTermsOfService(locale: string): Promise<TermsOfService> {
  const requestedLocale = TOS_LOCALES.includes(locale) ? locale : "en";

  if (requestedLocale !== "en") {
    const localized = await fetchTosDocument(requestedLocale);
    if (localized) {
      return { ...parseTos(localized), locale: requestedLocale };
    }
  }

  const english = await fetchTosDocument("en");
  if (english) {
    return { ...parseTos(english), locale: "en" };
  }

  return { ...parseTos(tosEnFallback), locale: "en" };
}
